// HR touch-point oversight with Maeve — Support Framework spec §15.
//
// HR picks a touch point and Maeve returns 3-5 oversight "checks" across the
// §15 areas (what to confirm, manager action, flags, policy/EAP/OH,
// continue/pause/escalate). Multi-turn: HR can ask follow-ups. This is HR's own
// process/oversight surface — NOT employee Maeve content (the HR firewall is
// about not exposing the employee's private conversations, which this never
// touches) — and everything Maeve returns is advisory.

import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { TouchpointSummary } from '@besideu/shared/cases';
import type {
  GuidanceTurn,
  TouchpointHrGuidanceArea,
  TouchpointHrGuidanceCheck,
  TouchpointHrGuidanceResponse,
} from '@besideu/shared/maeve';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from './ui/Button.tsx';

const TOUCHPOINT_LABELS: Record<TouchpointSummary['type'], string> = {
  Day_3: 'Day 3',
  Week_1: 'Week 1',
  Pre_Return: 'Pre-return',
  Week_1_Back: 'Week 1 back',
  Month_1: 'Month 1',
  Month_3: 'Month 3',
  Month_6: 'Month 6',
  Month_9: 'Month 9',
  Month_12: 'Month 12',
  Anniversary: 'Anniversary',
};

const AREA_LABELS: Record<TouchpointHrGuidanceArea, string> = {
  confirm: 'To confirm',
  manager_action: 'Manager action',
  flags: 'Flags & concerns',
  policy: 'Policy / EAP / OH',
  continuation: 'Continue, pause or escalate',
};

interface HrGuidanceApiResponse extends TouchpointHrGuidanceResponse {
  promptVersionId: string;
}

export function HrMaeveTouchpointGuidance({
  caseId,
  touchpoints,
}: {
  caseId: string;
  touchpoints: TouchpointSummary[];
}) {
  // Any non-Suppressed touch point is reviewable: HR oversight covers completed
  // ones (was it handled well?) as well as pending/overdue ones.
  const reviewable = useMemo(
    () => touchpoints.filter((t) => t.status !== 'Suppressed'),
    [touchpoints],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [history, setHistory] = useState<GuidanceTurn[]>([]);
  const [checks, setChecks] = useState<TouchpointHrGuidanceCheck[] | null>(null);
  const [followUp, setFollowUp] = useState('');

  const selected = useMemo(
    () => reviewable.find((t) => t.id === selectedId) ?? null,
    [reviewable, selectedId],
  );

  const announcement = useMemo(() => {
    const lastAssistant = [...history].reverse().find((t) => t.role === 'assistant');
    if (lastAssistant && lastAssistant.text !== 'See the oversight checks above.') {
      return `Maeve replied: ${lastAssistant.text}`;
    }
    if (checks) {
      return `Maeve has ${checks.length} oversight points: ${checks
        .map((c) => AREA_LABELS[c.area])
        .join('; ')}`;
    }
    return '';
  }, [history, checks]);

  const ask = useMutation({
    mutationFn: async ({
      touchpointId,
      text,
      history: priorHistory,
    }: {
      touchpointId: string;
      text: string;
      history: GuidanceTurn[];
    }) => {
      const userTurn: GuidanceTurn = { role: 'user', text };
      const resp = await api.post<HrGuidanceApiResponse>(
        `/api/hr/cases/${caseId}/maeve/touchpoint-guidance`,
        { touchpointId, messages: [...priorHistory, userTurn] },
      );
      const assistantTurn: GuidanceTurn = {
        role: 'assistant',
        text: resp.message ?? 'See the oversight checks above.',
      };
      return { user: userTurn, assistant: assistantTurn, checks: resp.checks };
    },
    onSuccess: ({ user, assistant, checks: next }) => {
      setHistory((prev) => [...prev, user, assistant]);
      setChecks(next);
      setFollowUp('');
    },
  });

  function startReview(touchpointId: string) {
    setSelectedId(touchpointId);
    setHistory([]);
    setChecks(null);
    setFollowUp('');
    ask.reset();
    ask.mutate({ touchpointId, text: 'Guide me through reviewing this touch point.', history: [] });
  }

  function submitFollowUp() {
    if (!selected) return;
    const trimmed = followUp.trim();
    if (trimmed.length === 0 || ask.isPending) return;
    ask.mutate({ touchpointId: selected.id, text: trimmed, history });
  }

  if (reviewable.length === 0) return null;

  return (
    <section className="space-y-4 rounded-lg border border-border p-6">
      <header>
        <h2 className="text-base font-semibold">Review a touch point with Maeve</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick a touch point; Maeve walks you through what to confirm, whether the manager has
          actioned it, any flags, relevant policy / EAP / occupational-health guidance, and whether
          the support pathway should continue, pause or escalate. Everything Maeve says is advisory.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {reviewable.map((t) => (
          <Button
            key={t.id}
            type="button"
            variant={t.id === selectedId ? 'primary' : 'secondary'}
            className="py-1.5 text-sm"
            onClick={() => t.id !== selectedId && startReview(t.id)}
            disabled={ask.isPending}
          >
            {TOUCHPOINT_LABELS[t.type]} · {t.scheduledFor}
            {t.isOverdue ? ' (overdue)' : ''}
          </Button>
        ))}
      </div>

      {selected ? (
        <>
          {ask.isPending && checks === null ? (
            <p className="text-sm text-muted-foreground" role="status">
              Maeve is reviewing the touch point…
            </p>
          ) : null}

          {checks ? (
            <ul className="space-y-2">
              {checks.map((c, i) => (
                <li key={i} className="rounded-md border border-border p-3 text-sm">
                  <p className="font-medium">{AREA_LABELS[c.area]}</p>
                  <p className="mt-1 text-muted-foreground">{c.guidance}</p>
                </li>
              ))}
            </ul>
          ) : null}

          <div aria-live="polite" className="sr-only">
            {announcement}
          </div>

          {ask.error ? (
            <p className="text-sm text-traffic-red" role="alert">
              {ask.error instanceof ApiError && ask.error.status === 503
                ? 'Maeve guidance isn’t available yet for this organisation.'
                : 'Something went wrong. Please try again.'}
            </p>
          ) : null}

          {checks ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitFollowUp();
              }}
              className="space-y-2"
            >
              <label className="block">
                <span className="sr-only">Follow-up question</span>
                <textarea
                  value={followUp}
                  onChange={(e) => setFollowUp(e.target.value)}
                  placeholder="e.g. The manager hasn't actioned this — what are my options?"
                  className="block w-full resize-y rounded-md border border-border bg-background p-3 text-base"
                  rows={2}
                  maxLength={4000}
                  disabled={ask.isPending}
                />
              </label>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  loading={ask.isPending}
                  disabled={followUp.trim().length === 0 || ask.isPending}
                  className="py-2 text-sm"
                >
                  Ask follow-up
                </Button>
              </div>
            </form>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
